import React from 'react'
import {type ListRenderItemInfo, View} from 'react-native'
import {
  AppBskyFeedDefs,
  type AppBskyFeedDefs as FeedDefs,
} from '@atproto/api'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {cleanError} from '#/lib/strings/errors'
import {useTapPostsQuery} from '#/state/queries/tap-posts'
import {Post} from '#/view/com/post/Post'
import {List, type ListMethods} from '#/view/com/util/List'
import {ListFooter, ListMaybePlaceholder} from '#/components/Lists'
import {useSetMinimalShellMode} from '#/state/shell'
import {MainScrollProvider} from '#/view/com/util/MainScrollProvider'
import {useHeaderOffset} from '#/components/hooks/useHeaderOffset'
import {LoadLatestBtn} from '#/view/com/util/load-latest/LoadLatestBtn'
import {s} from '#/lib/styles'
import {ComposeIcon2} from '#/lib/icons'
import {FAB} from '#/view/com/util/fab/FAB'
import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {useSession} from '#/state/session'
import {logEvent} from '#/lib/statsig/statsig'

type PostView = FeedDefs.PostView

const renderItem = ({item}: ListRenderItemInfo<PostView>) => {
  return <Post post={item} />
}

const keyExtractor = (item: PostView, index: number) => {
  return `${item.uri}-${index}`
}

const initialNumToRender = 14

export function TagFeedPage({
  testID,
  tagSlug,
  tagName,
  isPageFocused,
  isPageAdjacent,
}: {
  testID?: string
  tagSlug: string
  tagName: string
  isPageFocused: boolean
  isPageAdjacent: boolean
}) {
  const {_} = useLingui()
  const {hasSession} = useSession()
  const [isPTR, setIsPTR] = React.useState(false)
  const setMinimalShellMode = useSetMinimalShellMode()
  const headerOffset = useHeaderOffset()
  const {openComposer} = useOpenComposer()
  const scrollElRef = React.useRef<ListMethods>(null)
  const [hasNew, setHasNew] = React.useState(false)

  const {
    data,
    isFetched,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useTapPostsQuery(tagSlug, {
    enabled: isPageFocused || isPageAdjacent,
  })

  const posts = React.useMemo(() => {
    // TAP API returns AT Protocol-compliant PostView objects with viewer context
    // Frontend passes user's access token via Authorization header
    // Post-shadow system handles optimistic like/repost updates
    return data?.pages.flatMap(page => page.posts) || []
  }, [data])

  const onRefresh = React.useCallback(async () => {
    setIsPTR(true)
    await refetch()
    setIsPTR(false)
    setHasNew(false)
    logEvent('feed:refresh', {
      feedType: 'tag',
      feedUrl: `tag|${tagSlug}`,
      reason: 'pull-to-refresh',
    })
  }, [refetch, tagSlug])

  const onEndReached = React.useCallback(() => {
    if (isFetchingNextPage || !hasNextPage || error) return
    fetchNextPage()
  }, [isFetchingNextPage, hasNextPage, error, fetchNextPage])

  const scrollToTop = React.useCallback(() => {
    scrollElRef.current?.scrollToOffset({
      animated: true,
      offset: -headerOffset,
    })
    setMinimalShellMode(false)
  }, [headerOffset, setMinimalShellMode])

  const onPressLoadLatest = React.useCallback(() => {
    scrollToTop()
    refetch()
    setHasNew(false)
    logEvent('feed:refresh', {
      feedType: 'tag',
      feedUrl: `tag|${tagSlug}`,
      reason: 'load-latest',
    })
  }, [scrollToTop, refetch, tagSlug])

  const onPressCompose = React.useCallback(() => {
    openComposer({})
  }, [openComposer])

  return (
    <View testID={testID}>
      <MainScrollProvider>
        {posts.length < 1 ? (
          <ListMaybePlaceholder
            isLoading={isLoading || !isFetched}
            isError={isError}
            onRetry={refetch}
            emptyType="results"
            emptyMessage={_(msg`No posts found for this tag.`)}
            errorMessage={cleanError(error)}
          />
        ) : (
          <List
            ref={scrollElRef}
            data={posts}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            refreshing={isPTR}
            onRefresh={onRefresh}
            onEndReached={onEndReached}
            onEndReachedThreshold={4}
            // @ts-ignore web only -prf
            desktopFixedHeight
            ListFooterComponent={
              <ListFooter
                isFetchingNextPage={isFetchingNextPage}
                error={cleanError(error)}
                onRetry={fetchNextPage}
                hasNextPage={hasNextPage}
              />
            }
            initialNumToRender={initialNumToRender}
            windowSize={11}
          />
        )}
      </MainScrollProvider>

      {hasNew && (
        <LoadLatestBtn
          onPress={onPressLoadLatest}
          label={_(msg`Load new posts`)}
          showIndicator={hasNew}
        />
      )}

      {hasSession && (
        <FAB
          testID="composeFAB"
          onPress={onPressCompose}
          icon={<ComposeIcon2 strokeWidth={1.5} size={29} style={s.white} />}
          accessibilityRole="button"
          accessibilityLabel={_(msg({message: `New post`, context: 'action'}))}
          accessibilityHint=""
        />
      )}
    </View>
  )
}
